import React, { useState, useEffect } from 'react';
import { t } from '../../i18n';
import { useNavigate } from 'react-router-dom';
import { realtyApi, isRealtyLoggedIn } from './realtyApi';

const TYPES = [
  ['house', 'House'], ['flat', 'Flat/Apartment'], ['upper_portion', 'Upper Portion'],
  ['lower_portion', 'Lower Portion'], ['farm_house', 'Farm House'], ['room', 'Room'],
  ['plot_residential', 'Residential Plot'], ['plot_commercial', 'Commercial Plot'],
  ['office', 'Office'], ['shop', 'Shop'], ['warehouse', 'Warehouse'],
  ['building', 'Building'], ['factory', 'Factory'],
];

export default function RealtyPostAd() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [images, setImages] = useState([]);
  const [hasBlueTick, setHasBlueTick] = useState(false);
  const [autoPost, setAutoPost] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', purpose: 'sale', category: 'residential',
    property_type: 'house', price: '', city: '', area: '', address: '',
    area_size: '', area_unit: 'Marla', bedrooms: '', bathrooms: '', phone: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/blue-tick/', { headers: { Authorization: `Token ${token}` } })
      .then((r) => r.json())
      .then((d) => setHasBlueTick(!!d.has_blue_tick))
      .catch(() => {});
  }, []);

  if (!isRealtyLoggedIn()) {
    return (
      <div className="realty-scope form-page">
        <div className="form-card">
          <h2 style={{ marginBottom: 10 }}>{t('login_required')}</h2>
          <p className="muted">{t('open_in_pi_browser')}</p>
        </div>
      </div>
    );
  }

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('auto_post', autoPost ? 'true' : 'false');
      images.forEach((img) => fd.append('uploaded_images', img));
      const created = await realtyApi.createProperty(fd);
      navigate(`/realty/property/${created.id}`);
    } catch (e2) {
      setError('Could not post listing. Please check all required fields.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="realty-scope form-page">
      <h2 style={{ marginBottom: 4 }}>{t('post_your_property')}</h2>
      <p className="muted" style={{ marginBottom: 18 }}>
        Free to list. XpertRealty takes a 5% commission only when your deal closes on the platform.
      </p>

      {error && <div className="error-box">{error}</div>}

      <form className="form-card" onSubmit={handleSubmit}>
        <div className="field">
          <label>{t('title_label')}</label>
          <input required placeholder="e.g. 5 Marla House in DHA Phase 5" value={form.title} onChange={(e) => update('title', e.target.value)} />
        </div>

        <div className="field-row">
          <div className="field">
            <label>{t('purpose_label')}</label>
            <select value={form.purpose} onChange={(e) => update('purpose', e.target.value)}>
              <option value="sale">{t('for_sale')}</option>
              <option value="rent">{t('for_rent')}</option>
            </select>
          </div>
          <div className="field">
            <label>{t('category_label')}</label>
            <select value={form.category} onChange={(e) => update('category', e.target.value)}>
              <option value="residential">{t('residential')}</option>
              <option value="commercial">{t('commercial')}</option>
            </select>
          </div>
        </div>

        <div className="field">
          <label>{t('property_type')}</label>
          <select value={form.property_type} onChange={(e) => update('property_type', e.target.value)}>
            {TYPES.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
          </select>
        </div>

        <div className="field-row">
          <div className="field">
            <label>Price (PKR)</label>
            <input required type="number" value={form.price} onChange={(e) => update('price', e.target.value)} />
          </div>
          <div className="field">
            <label>{t('your_phone')}</label>
            <input required value={form.phone} onChange={(e) => update('phone', e.target.value)} />
          </div>
        </div>

        <div className="field-row">
          <div className="field">
            <label>{t('city')}</label>
            <input required value={form.city} onChange={(e) => update('city', e.target.value)} />
          </div>
          <div className="field">
            <label>Area / Locality</label>
            <input required value={form.area} onChange={(e) => update('area', e.target.value)} />
          </div>
        </div>

        <div className="field">
          <label>Full address (optional)</label>
          <input value={form.address} onChange={(e) => update('address', e.target.value)} />
        </div>

        <div className="field-row">
          <div className="field">
            <label>{t('size_label')}</label>
            <input required type="number" value={form.area_size} onChange={(e) => update('area_size', e.target.value)} />
          </div>
          <div className="field">
            <label>{t('unit_label')}</label>
            <select value={form.area_unit} onChange={(e) => update('area_unit', e.target.value)}>
              <option>Marla</option><option>Kanal</option><option>Sq. Ft.</option><option>Sq. Yd.</option><option>Acre</option>
            </select>
          </div>
        </div>

        <div className="field-row">
          <div className="field">
            <label>Bedrooms (optional)</label>
            <input type="number" value={form.bedrooms} onChange={(e) => update('bedrooms', e.target.value)} />
          </div>
          <div className="field">
            <label>Bathrooms (optional)</label>
            <input type="number" value={form.bathrooms} onChange={(e) => update('bathrooms', e.target.value)} />
          </div>
        </div>

        <div className="field">
          <label>{t('description')}</label>
          <textarea required rows="4" value={form.description} onChange={(e) => update('description', e.target.value)} />
        </div>

        <div className="field">
          <label>{t('photos_label')}</label>
          <input type="file" multiple accept="image/*" onChange={(e) => setImages(Array.from(e.target.files))} />
        </div>

        {hasBlueTick && (
          <div className="field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" id="autopost" checked={autoPost} onChange={(e) => setAutoPost(e.target.checked)} />
            <label htmlFor="autopost" style={{ margin: 0, fontSize: 13 }}>📢 Auto-post to PenFlow (Blue Tick perk)</label>
          </div>
        )}

        <button className="btn btn-primary" style={{ width: '100%' }} disabled={submitting}>
          {submitting ? 'Posting...' : 'Post listing'}
        </button>
      </form>
    </div>
  );
}
